import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import moment from 'moment-timezone';
import {
  Button,
  IconLogo,
  TextAreaSimple,
  TranslationProvider,
  getTranslation,
} from '@resy/ui-kitchen';
import { setSearchParams } from '../../../../../ui-kitchen/src/utils/searchParamUtils';
import useExperiment from '../../../../../ui-kitchen/src/components/Experiments/useExperiment';
import { RESY_WEB_ENABLE_2025_CODE_FREEZE_BUG_174 } from '../../../../../ui-kitchen/src/constants/optimizelyExperiments';

import RatingFormThanks from './RatingFormThanks';
import RatingFormErrors from './RatingFormErrors';
import RatingFormModals from './RatingFormModals';

import Spinner from '../../../images/icons/processing-spinner.svg';
import StarIcon from '../../../images/rating-star-off-outline.svg';
import FoodIcon from '../../../images/icon-food.svg';
import ServiceIcon from '../../../images/icon-service.svg';
import VibeIcon from '../../../images/icon-vibe.svg';
import OtherIcon from '../../../images/icon-other.svg';

import './RatingForm.scss';

const RatingForm = ({
  Analytics,
  loading,
  showThanks,
  showError,
  initialRating,
  venue,
  date,
  errorStatus,
  submitRating,
}) => {
  const [state, setState] = useState({
    rating: initialRating,
    tags: [],
    comments: '',
    errors: false,
    showErrorModal: false,
  });

  const [isCodeFreezeFlagOn] = useExperiment(RESY_WEB_ENABLE_2025_CODE_FREEZE_BUG_174);

  useEffect(() => {
    let errors = null;

    if (!state.rating) {
      errors = true;
    } else if (state.rating <= 2 && state.comments === '') {
      errors = true;
    } else {
      errors = false;
    }

    setState((prevState) => ({
      ...prevState,
      errors,
    }));
  }, [state.rating, state.comments]);

  useEffect(() => () => {
    if (isCodeFreezeFlagOn) {
      setSearchParams('resyToken', null);
      setSearchParams('venue', null);
    }
  }, []);

  const onStarClick = (num) => {
    setState((prevState) => ({
      ...prevState,
      rating: num,
    }));
  };

  const onDriverClick = (tag) => {
    const index = state.tags.indexOf(tag);
    const driverArray = Object.assign([], state.tags);

    if (index !== -1) {
      driverArray.splice(index, 1);
    } else {
      driverArray.push(tag);
    }

    setState((prevState) => ({
      ...prevState,
      tags: driverArray,
    }));
  };

  const onCommentChange = (comment) => {
    setState((prevState) => ({
      ...prevState,
      comments: comment,
    }));
  };

  const onFormSubmit = () => {
    if (state.errors) {
      setState((prevState) => ({
        ...prevState,
        showErrorModal: true,
      }));
    } else {
      submitRating({
        rating: state.rating,
        tags: state.tags,
        comments: state.comments,
      });
    }
  };

  const onCloseModal = () => {
    Analytics.event('dismiss rating', {
      dismiss_type: !state.rating ? 'rating_required' : 'comment_required',
      rating_value: state.rating || undefined,
      rating_scale: 5,
      venue,
    });
    setState((prevState) => ({
      ...prevState,
      showErrorModal: false,
    }));
  };

  const renderStars = (disabled = false) => {
    const stars = [
      {
        name: getTranslation({ translationKey: 'rate_1_star', fallback: 'One Star' }),
        rating: 1,
      },
      {
        name: getTranslation({ translationKey: 'rate_2_stars', fallback: 'Two Star' }),
        rating: 2,
      },
      {
        name: getTranslation({ translationKey: 'rate_3_stars', fallback: 'Three Star' }),
        rating: 3,
      },
      {
        name: getTranslation({ translationKey: 'rate_4_stars', fallback: 'Four Star' }),
        rating: 4,
      },
      {
        name: getTranslation({ translationKey: 'rate_5_stars', fallback: 'Five Star' }),
        rating: 5,
      },
    ];

    return (
      <>
        {stars.map((star) => (
          <Button
            className={cx('RatingForm__star', {
              active: (state.rating >= star.rating),
            })}
            disabled={disabled}
            variant="no-style"
            onClick={() => {
              if (!disabled) return onStarClick(star.rating);
              return false;
            }}
            data-test-id={`rating_form-button-rating_${star.rating}`}
          >
            <StarIcon
              width={40}
              height={40}
            />
            <span>{star.name}</span>
          </Button>
        ))}
      </>
    );
  };

  const renderDrivers = () => {
    const driverArray = [
      {
        key: 'food',
        name: getTranslation({ translationKey: 'rate_reservation_tags_food', fallback: 'Food' }),
        svg: () => <FoodIcon />,
      },
      {
        key: 'service',
        name: getTranslation({ translationKey: 'rate_reservation_tags_service', fallback: 'Service' }),
        svg: () => <ServiceIcon />,
      },
      {
        key: 'vibe',
        name: getTranslation({ translationKey: 'rate_reservation_tags_vibe', fallback: 'Vibe' }),
        svg: () => <VibeIcon />,
      },
      {
        key: 'other',
        name: getTranslation({ translationKey: 'rate_reservation_tags_other', fallback: 'Other' }),
        svg: () => <OtherIcon />,
      },
    ];

    return (
      <>
        {driverArray.map((item) => (
          <Button
            className={cx(
              'RatingForm__driver',
              `RatingForm__driver-${item.key}`,
              {
                active: (state.tags.indexOf(item.key) > -1),
              }
            )}
            variant="no-style"
            onClick={() => onDriverClick(item.key)}
            data-test-id={`rating_form-button-${item.key}`}
          >
            <div className="RatingForm__driver-svg-wrapper">
              {item.svg()}
            </div>
            <div className="RatingForm__driver-label">{item.name}</div>
          </Button>
        ))}
      </>
    );
  };

  if (loading) {
    return (
      <div className="RatingForm">
        <div className="RatingForm__head RatingForm__row">
          <IconLogo />
        </div>
        <Spinner
          className="Icon--loading"
          aria-hidden="true"
          width={33}
          height={32}
        />
      </div>
    );
  }

  if (showError) {
    return (
      <RatingFormErrors errorStatus={errorStatus} />
    );
  }

  if (showThanks) {
    return (
      <RatingFormThanks
        venue={venue}
        rating={state.rating}
        renderStars={renderStars}
      />
    );
  }

  return (
    <div className="RatingForm">
      <RatingFormModals
        rating={state.rating}
        showErrorModal={state.showErrorModal}
        onCloseModal={onCloseModal}
      />
      <div className="RatingForm__head RatingForm__row">
        <IconLogo />
        <TranslationProvider
          translationKey="rate_reservation_header"
          translationValues={{ venueName: venue }}
        >
          <h1>
            {`How was ${venue}?`}
          </h1>
        </TranslationProvider>

        {date
          ? (
            <TranslationProvider
              translationKey="rate_reservation_subhead"
              translationValues={{ date: moment(date).format('MMM Do') }}
            >
              <p>
                {`We appreciate feedback about your experience on ${moment(date).format('MMM Do')}.`}
              </p>
            </TranslationProvider>
          )
          : (
            <TranslationProvider
              translationKey="rate_reservation_subhead_no_date"
            >
              <p>
                We appreciate feedback about your experience.
              </p>
            </TranslationProvider>
          )}
      </div>
      <hr />

      <div className="RatingForm__rating RatingForm__row">
        <h2 className="RatingForm__label">
          <TranslationProvider
            translationKey="rate_reservation_rating_label"
            translationValues={{ venueName: venue }}
          >
            <span>
              {`How likely are you to recommend ${venue} to others?`}
            </span>
          </TranslationProvider>
        </h2>
        <div className="RatingForm__rating-stars">
          {renderStars()}
        </div>
        <div className="RatingForm__rating-legend">
          <TranslationProvider translationKey="rate_reservation_rating_negative">
            <div>Not Likely</div>
          </TranslationProvider>
          <div className="divider" />
          <TranslationProvider translationKey="rate_reservation_rating_positive">
            <div>Very Likely</div>
          </TranslationProvider>
        </div>
      </div>
      <hr />

      <div className="RatingForm__drivers RatingForm__row">
        <h2 className="RatingForm__label">
          <TranslationProvider translationKey="rate_reservation_tags_label">
            <span>What’s driving your review?</span>
          </TranslationProvider>
          &nbsp;
          <span className="RatingForm__label-grey">
            (
            <TranslationProvider translationKey="optional">
              <span>
                optional
              </span>
            </TranslationProvider>
            )
          </span>
        </h2>
        <div className="RatingForm__driver-buttons">
          {renderDrivers()}
        </div>
      </div>
      <hr />

      <div className="RatingForm__comments RatingForm__row">
        {(state.rating && state.rating <= 2)
          && (
            <h2 className="RatingForm__label">
              <TranslationProvider translationKey="rate_reservation_comments_label_negative">
                <span>What went wrong?</span>
              </TranslationProvider>
              &nbsp;
              <span className="RatingForm__label-grey">
                (
                <TranslationProvider translationKey="required">
                  <span>
                    required
                  </span>
                </TranslationProvider>
                )
              </span>
            </h2>
          )}
        {
          (!state.rating || state.rating > 2) && (
            <h2 className="RatingForm__label">
              <TranslationProvider translationKey="rate_reservation_comments_label">
                <span>Anything else?</span>
              </TranslationProvider>
              &nbsp;
              <span className="RatingForm__label-grey">
                (
                <TranslationProvider translationKey="optional">
                  <span>
                    optional
                  </span>
                </TranslationProvider>
                )
              </span>

            </h2>
          )
        }
        <TranslationProvider
          translationKey="rate_reservation_comments_body"
          translationValues={{ venueName: venue }}
        >
          <p>
            {`We’ll share your feedback and contact info with ${venue}.`}
          </p>
        </TranslationProvider>
        <div>
          <TextAreaSimple
            placeholder={getTranslation({
              translationKey: 'rate_reservation_comments_placeholder',
              fallback: 'Share your experience or offer suggestions to the staff…',
            })}
            value={state.comments}
            onChange={(e) => onCommentChange(e.target.value)}
          />
        </div>
      </div>

      <div className="RatingForm__ctas RatingForm__row">
        <TranslationProvider translationKey="rate_reservation_submit">
          <Button
            lg
            onClick={onFormSubmit}
            data-test-id="rating_form-button-submit"
          >
            Submit Review
          </Button>
        </TranslationProvider>
      </div>
    </div>
  );
};

const ratingFormPropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }),
  initialRating: PropTypes.number,
  loading: PropTypes.bool,
  showThanks: PropTypes.bool,
  showError: PropTypes.bool,
  venue: PropTypes.string,
  date: PropTypes.string,
  errorStatus: PropTypes.string,
  submitRating: PropTypes.func,
};

RatingForm.propTypes = ratingFormPropTypes;

RatingForm.defaultProps = {
  Analytics: {
    event: () => {},
  },
  loading: true,
  initialRating: null,
  showThanks: false,
  showError: false,
  venue: null,
  date: null,
  errorStatus: null,
  submitRating: () => { },
};

RatingForm.displayName = 'RatingForm';
export { ratingFormPropTypes, RatingForm as ComponentUnderTest };
export default RatingForm;
